// parseReportSections.js - Module for parsing report sections with simplified title detection

/**
 * Parse report text into sections based on title detection rules:
 * - Title must have an empty line BEFORE it OR both empty lines before AND after (single line section)
 * - Title must be max 5 words OR max 50 characters
 * - Title must NOT end with a dot (.)
 * - NEW: Lines with colons can create inline sections (Title: content)
 * 
 * @param {string} reportText - The report text to parse
 * @returns {Array<{title: string, content: string}>} Array of sections
 */
export function parseReportSections(reportText) {
  if (!reportText || typeof reportText !== 'string') {
    return [];
  }

  const lines = reportText.split('\n');
  const sections = [];
  let currentSection = null;
  let sectionCounter = 1;
  let inClassicSection = false; // Track if we're in a classic section

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Check if current line could be a title
    if (trimmedLine !== '') {
      // Check if previous line was empty (or we're at the start)
      const previousLineEmpty = i === 0 || lines[i - 1].trim() === '';
      // Check if next line is empty (or we're at the end)
      const nextLineEmpty = i === lines.length - 1 || lines[i + 1].trim() === '';
      
      // NEW: Check for colon-based inline section FIRST (Title: content)
      // Only apply if not already in a classic section content
      const colonIndex = trimmedLine.indexOf(':');
      const hasColon = colonIndex > 0 && colonIndex < trimmedLine.length - 1;
      let isColonTitle = false;
      
      if (hasColon && !inClassicSection) {
        const beforeColon = trimmedLine.substring(0, colonIndex).trim();
        const afterColon = trimmedLine.substring(colonIndex + 1).trim();
        
        // Check if the part before colon could be a title
        // (relatively short, no dots, and has content after)
        const beforeColonWords = beforeColon.split(/\s+/).length;
        const beforeColonChars = beforeColon.length;
        
        // Be more lenient with colon titles - they just need to be reasonable length
        isColonTitle = afterColon.length > 0 && 
                       !beforeColon.endsWith('.') &&
                       (beforeColonWords <= 5 || beforeColonChars <= 50);
      }

      // Classic title detection criteria
      const wordCount = trimmedLine.split(/\s+/).length;
      const charCount = trimmedLine.length;
      const hasNoDot = !trimmedLine.endsWith('.');
      
      // A line is a classic title if:
      // 1. Has empty line before AND (has ≤5 words OR ≤50 chars) AND no dot
      // 2. OR: Has empty lines both before AND after (single line section title)
      // 3. BUT NOT if it contains a colon that would make it a colon title
      const isClassicTitle = !isColonTitle && hasNoDot && (wordCount <= 5 || charCount <= 50) && 
                      (previousLineEmpty || (previousLineEmpty && nextLineEmpty));

      if (isColonTitle) {
        // Colon-based inline section - PROCESS FIRST
        const colonIndex = trimmedLine.indexOf(':');
        const title = trimmedLine.substring(0, colonIndex).trim();
        const content = trimmedLine.substring(colonIndex + 1).trim();
        
        // Save previous section if exists
        if (currentSection) {
          currentSection.content = currentSection.content.trimEnd();
          sections.push(currentSection);
        }
        
        // Create a new section from the colon-separated content
        sections.push({
          title: title,
          content: content
        });
        
        // Reset current section
        currentSection = null;
        inClassicSection = false;
      } else if (isClassicTitle) {
        // Classic title detection
        inClassicSection = true;
        
        // Save previous section if exists
        if (currentSection) {
          // Only add if there's content or it's a title-only section
          if (currentSection.content.trim() || sections.length === 0) {
            currentSection.content = currentSection.content.trimEnd();
            sections.push(currentSection);
          }
        }
        
        // Start new section
        currentSection = {
          title: trimmedLine,
          content: ''
        };
      } else {
        // Regular content line
        if (!currentSection) {
          // If no section yet, create a default one with "Section 1"
          currentSection = {
            title: `Section ${sectionCounter}`,
            content: ''
          };
          sectionCounter++;
          inClassicSection = false;
        }
        
        // Add the line to content
        if (currentSection.content) {
          currentSection.content += '\n' + line;
        } else {
          currentSection.content = line;
        }
      }
    } else {
      // Empty line - just add to current section if exists
      if (currentSection && currentSection.content) {
        currentSection.content += '\n';
      }
      // Empty line ends a classic section context
      inClassicSection = false;
    }
  }

  // Add the last section if exists
  if (currentSection) {
    // Add even if empty content (could be a title-only section)
    currentSection.content = currentSection.content.trimEnd();
    sections.push(currentSection);
  }

  return sections;
}

/**
 * Parse report with configurable parameters
 * 
 * @param {string} reportText - The report text to parse
 * @param {Object} options - Configuration options
 * @param {number} options.maxWords - Maximum words for title (default: 5)
 * @param {number} options.maxChars - Maximum characters for title (default: 50)
 * @param {boolean} options.excludeDotsAtEnd - Exclude lines ending with dots (default: true)
 * @param {string} options.defaultSectionTitle - Default title for untitled sections (default: 'Section')
 * @param {boolean} options.detectColonSections - Enable colon-based section detection (default: true)
 * @returns {Array<{title: string, content: string}>} Array of sections
 */
export function parseReportSectionsConfigurable(reportText, options = {}) {
  const {
    maxWords = 5,
    maxChars = 50,
    excludeDotsAtEnd = true,
    defaultSectionTitle = 'Section',
    detectColonSections = true
  } = options;

  if (!reportText || typeof reportText !== 'string') {
    return [];
  }

  const lines = reportText.split('\n');
  const sections = [];
  let currentSection = null;
  let sectionCounter = 1;
  let inClassicSection = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    if (trimmedLine !== '') {
      // Check if previous line was empty (or we're at the start)
      const previousLineEmpty = i === 0 || lines[i - 1].trim() === '';
      // Check if next line is empty (or we're at the end)
      const nextLineEmpty = i === lines.length - 1 || lines[i + 1].trim() === '';
      
      // Colon-based inline section detection FIRST
      let isColonTitle = false;
      if (detectColonSections && !inClassicSection) {
        const colonIndex = trimmedLine.indexOf(':');
        if (colonIndex > 0 && colonIndex < trimmedLine.length - 1) {
          const beforeColon = trimmedLine.substring(0, colonIndex).trim();
          const afterColon = trimmedLine.substring(colonIndex + 1).trim();
          
          const beforeColonWords = beforeColon.split(/\s+/).length;
          const beforeColonChars = beforeColon.length;
          
          isColonTitle = afterColon.length > 0 && 
                         (!excludeDotsAtEnd || !beforeColon.endsWith('.')) &&
                         (beforeColonWords <= maxWords || beforeColonChars <= maxChars);
        }
      }
      
      // Title detection criteria
      const wordCount = trimmedLine.split(/\s+/).length;
      const charCount = trimmedLine.length;
      const hasNoDot = !excludeDotsAtEnd || !trimmedLine.endsWith('.');
      
      // Classic title detection (only if not a colon title)
      const isClassicTitle = !isColonTitle && hasNoDot && (wordCount <= maxWords || charCount <= maxChars) && 
                      (previousLineEmpty || (previousLineEmpty && nextLineEmpty));

      if (isColonTitle) {
        const colonIndex = trimmedLine.indexOf(':');
        const title = trimmedLine.substring(0, colonIndex).trim();
        const content = trimmedLine.substring(colonIndex + 1).trim();
        
        if (currentSection) {
          currentSection.content = currentSection.content.trimEnd();
          sections.push(currentSection);
        }
        
        sections.push({
          title: title,
          content: content
        });
        
        currentSection = null;
        inClassicSection = false;
      } else if (isClassicTitle) {
        inClassicSection = true;
        
        if (currentSection) {
          currentSection.content = currentSection.content.trimEnd();
          sections.push(currentSection);
        }
        
        currentSection = {
          title: trimmedLine,
          content: ''
        };
      } else {
        if (!currentSection) {
          currentSection = {
            title: `${defaultSectionTitle} ${sectionCounter}`,
            content: ''
          };
          sectionCounter++;
          inClassicSection = false;
        }
        
        if (currentSection.content) {
          currentSection.content += '\n' + line;
        } else {
          currentSection.content = line;
        }
      }
    } else {
      if (currentSection && currentSection.content) {
        currentSection.content += '\n';
      }
      inClassicSection = false;
    }
  }

  if (currentSection) {
    currentSection.content = currentSection.content.trimEnd();
    sections.push(currentSection);
  }

  return sections;
}

/**
 * Check if a line should be considered a title based on its position and content
 * 
 * @param {string} line - The line to check
 * @param {boolean} previousLineEmpty - Whether the previous line was empty
 * @param {Object} options - Optional configuration
 * @returns {boolean} True if the line should be a title
 */
export function shouldBeTitle(line, previousLineEmpty = true, options = {}) {
  const {
    maxWords = 5,
    maxChars = 50,
    excludeDotsAtEnd = true,
    checkColonTitle = true
  } = options;

  const trimmedLine = line.trim();
  
  if (!trimmedLine) return false;
  
  // Check colon-based title format FIRST
  if (checkColonTitle) {
    const colonIndex = trimmedLine.indexOf(':');
    if (colonIndex > 0 && colonIndex < trimmedLine.length - 1) {
      const beforeColon = trimmedLine.substring(0, colonIndex).trim();
      const afterColon = trimmedLine.substring(colonIndex + 1).trim();
      
      const beforeColonWords = beforeColon.split(/\s+/).length;
      const beforeColonChars = beforeColon.length;
      
      if (afterColon.length > 0 && 
          (!excludeDotsAtEnd || !beforeColon.endsWith('.')) &&
          (beforeColonWords <= maxWords || beforeColonChars <= maxChars)) {
        return true;
      }
    }
  }
  
  // Check classic title format (only if not a colon title)
  if (previousLineEmpty) {
    const wordCount = trimmedLine.split(/\s+/).length;
    const charCount = trimmedLine.length;
    const hasNoDot = !excludeDotsAtEnd || !trimmedLine.endsWith('.');
    
    if ((wordCount <= maxWords || charCount <= maxChars) && hasNoDot) {
      return true;
    }
  }
  
  return false;
}

/**
 * Parse mail sections with specific email structure detection
 * Uses the same title detection logic but with email-specific defaults
 * 
 * @param {string} mailText - The mail text to parse
 * @returns {Object} Object with Email, Objet, and Corps fields
 */
export function parseMailSections(mailText) {
  const sections = parseReportSections(mailText);
  const mailStructure = {
    Email: '',
    Objet: '',
    Corps: ''
  };

  let emailFound = false;
  let objetFound = false;
  const bodyParts = [];

  sections.forEach((section) => {
    const titleLower = section.title.toLowerCase();

    const isEmail = (
      titleLower.includes('email') ||
      titleLower.includes('à') ||
      titleLower.includes('destinataire') ||
      titleLower.includes('email :') ||
      titleLower.includes('email:') ||
      titleLower.includes('mail :') ||
      titleLower.includes('mail:')
    );

    const isObjet = (
      titleLower.includes('objet') ||
      titleLower.includes('sujet') ||
      titleLower.includes('objet :') ||
      titleLower.includes('objet:') ||
      titleLower.includes('subjet :') ||
      titleLower.includes('subjet:')
    );

    if (isEmail) {
      if (!emailFound) mailStructure.Email = section.content.trim();
      emailFound = true; // exclude from body
      return;
    }

    if (isObjet) {
      if (!objetFound) mailStructure.Objet = section.content.trim();
      objetFound = true; // exclude from body
      return;
    }

    const title = (section.title || '').trim();
    const content = (section.content || '').trim();
    const isAutoGeneratedTitle = /^section\s+\d+$/i.test(title);
    if (title && content) {
      if (isAutoGeneratedTitle) {
        bodyParts.push(content);
      } else {
        bodyParts.push(`${title}\n\n${content}`);
      }
    } else if (title) {
      if (!isAutoGeneratedTitle) {
        bodyParts.push(title);
      }
    } else if (content) {
      bodyParts.push(content);
    }
  });

  if (emailFound || objetFound) {
    mailStructure.Corps = bodyParts.join('\n\n');
  } else {
    // Fallback: if neither Email nor Objet detected via sections, remove obvious header lines from raw text
    const lines = (mailText || '').split('\n');
    const filtered = lines.filter((rawLine) => {
      const l = rawLine.trim().toLowerCase();
      if (!l) return true;
      return !(
        l.startsWith('email') ||
        l.startsWith('mail') ||
        l.startsWith('à ') ||
        l.startsWith('a:') ||
        l.startsWith('destinataire') ||
        l.startsWith('objet') ||
        l.startsWith('sujet') ||
        /^section\s+\d+$/.test(l)
      );
    });
    mailStructure.Corps = filtered.join('\n').trim();
  }

  return mailStructure;
}



/**
 * Convert an array of sections to a CSV string with columns: key;content
 * Values are quoted and quotes are escaped for Excel compatibility.
 * Uses ';' as delimiter to better support locales where Excel expects semicolons.
 * 
 * @param {Array<{title: string, content: string}>} sections
 * @param {string} delimiter
 * @returns {string}
 */
export function sectionsToCSV(sections, delimiter = ';') {
  const escapeForCSV = (value) => {
    const str = (value ?? '').toString();
    const needsQuoting = /["\n\r]/.test(str) || str.includes(delimiter);
    const escaped = str.replace(/"/g, '""');
    return needsQuoting ? `"${escaped}"` : escaped;
  };

  const keys = (sections || []).map((s) => (s && typeof s.title === 'string') ? s.title : '');
  const contents = (sections || []).map((s) => (s && typeof s.content === 'string') ? s.content : '');

  const keysLine = keys.map(escapeForCSV).join(delimiter);
  const contentsLine = contents.map(escapeForCSV).join(delimiter);

  // Two-row CSV: first line titles, second line contents (CRLF for Excel)
  return [keysLine, contentsLine].join('\r\n');
}

/**
 * Trigger a client-side download of a CSV string as a file.
 * Adds BOM for proper Excel opening.
 * 
 * @param {string} csvString
 * @param {string} filename
 */
export function downloadCSV(csvString, filename = 'sections.csv') {
  try {
    const blob = new Blob(["\uFEFF" + (csvString || '')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return blob;
  } catch (e) {
    // Re-throw so callers can handle UI feedback
    throw e;
  }
}

/**
 * Parse report text into sections and download as CSV (key;content)
 * 
 * @param {string} reportText
 * @param {string} filename
 * @param {string} delimiter
 * @returns {Blob|undefined}
 */
export function downloadSectionsCSVFromText(reportText, filename = 'sections.csv', delimiter = ';') {
  const sections = parseReportSections(reportText || '');
  const csv = sectionsToCSV(sections, delimiter);
  return downloadCSV(csv, filename);
}

export default { 
  parseReportSections, 
  parseMailSections, 
  parseReportSectionsConfigurable,
  sectionsToCSV,
  downloadCSV,
  downloadSectionsCSVFromText
};